import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatMoney } from "../../shared/money";
import { hasPermission } from "../../shared/permissions";
import { api, eurosToMinor, minorToEuros } from "../api";
import { useSession } from "../App";
import { Banner, messageOf, onSubmit } from "./ui";

type SpaceRange = { id: string; name: string; min_guests: number | null; max_guests: number | null };

function guestRange(space: { min_guests: number | null; max_guests: number | null }): string {
  if (space.min_guests != null && space.max_guests != null) return `${space.min_guests}–${space.max_guests} άτομα`;
  if (space.max_guests != null) return `έως ${space.max_guests} άτομα`;
  if (space.min_guests != null) return `από ${space.min_guests} άτομα`;
  return "Δεν έχει οριστεί εύρος ατόμων";
}

type VenueRow = { id: string; name: string; ownership: string; area: string | null; status: string; spaces?: SpaceRange[] };

export function VenuesPage() {
  const [venues, setVenues] = useState<VenueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const { user } = useSession();
  const canWrite = hasPermission(user.role, user.permissions, "rates.write");

  function load() {
    api<{ venues: VenueRow[] }>("/api/venues")
      .then((result) => setVenues(result.venues))
      .catch((reason: unknown) => setError(messageOf(reason)));
  }

  useEffect(load, []);

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Χώροι</h1>
          <p className="lede">Οι συνεργαζόμενοι χώροι με κενό ημερολόγιο μένουν άγνωστοι. Οι δικοί μας χώροι ακολουθούν τους κανόνες και τα παράθυρα που δημοσιεύεις.</p>
        </div>
      </header>
      <Banner error={error} note={note} />
      {!venues && !error ? <p>Φόρτωση χώρων…</p> : null}
      {venues && venues.length === 0 ? <p>Δεν υπάρχουν χώροι ακόμη.</p> : null}
      <div className="grid cards">
        {(venues ?? []).map((venue) => (
          <Link className="card" key={venue.id} to={`/venues/${venue.id}`}>
            <span>{venue.ownership === "partner" ? "Συνεργάτης" : "Εσωτερικός"} · {venue.area || "Χωρίς περιοχή"}</span>
            <b style={{ fontSize: 24 }}>{venue.name}</b>
            <span>{venue.spaces?.map((space) => guestRange(space)).join(" · ") || "Δεν έχει οριστεί εύρος ατόμων"}</span>
          </Link>
        ))}
      </div>
      {canWrite ? (
        <form
          className="panel"
          style={{ marginTop: 16 }}
          onSubmit={onSubmit(async (body) => {
            setError(null);
            try {
              await api("/api/venues", { method: "POST", body: JSON.stringify({ name: body.name, ownership: body.ownership, area: body.area }) });
              setNote(`Ο χώρος ${body.name} προστέθηκε.`);
              load();
            } catch (reason) {
              setError(messageOf(reason));
            }
          })}
        >
          <h2>Νέος χώρος</h2>
          <div className="fields">
            <label>Ονομα<input name="name" required /></label>
            <label>Ιδιοκτησία
              <select name="ownership" defaultValue="internal"><option value="internal">Εσωτερικός</option><option value="partner">Συνεργάτης</option></select>
            </label>
            <label>Περιοχή<input name="area" /></label>
          </div>
          <button type="submit">Αποθήκευση χώρου</button>
        </form>
      ) : null}
    </>
  );
}

type RateRow = {
  id: string;
  name: string;
  charge_role: string;
  unit: string;
  version_id: string | null;
  version_number: number | null;
  selling_price_minor: number | null;
  internal_cost_minor: number | null;
  valid_from: string | null;
  valid_to: string | null;
  days_of_week: string | null;
  season_label: string | null;
  version_tax: string | null;
  version_status: string | null;
};

type VenueDetail = {
  venue: { id: string; name: string; area: string | null; notes: string | null; contact_email: string | null; restrictions: string | null; version: number; ownership: string };
  spaces: { id: string; name: string; exclusivity: string; min_guests: number | null; max_guests: number | null; version: number }[];
  rates: RateRow[];
};

export function VenuePage() {
  const { id = "" } = useParams();
  const { user } = useSession();
  const [detail, setDetail] = useState<VenueDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const canPublish = hasPermission(user.role, user.permissions, "rates.publish");
  const canWrite = hasPermission(user.role, user.permissions, "rates.write");
  const financials = hasPermission(user.role, user.permissions, "financials.read");

  function load() {
    api<VenueDetail>(`/api/venues/${id}`)
      .then((result) => {
        setDetail(result);
        setDirty(false);
      })
      .catch((reason: unknown) => setError(messageOf(reason)));
  }

  useEffect(load, [id]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const published = new Map<string, RateRow>();
  for (const rate of detail?.rates ?? []) {
    if (rate.version_status === "published" || !published.has(rate.id)) published.set(rate.id, rate);
  }

  return (
    <>
      <header className="page-head">
        <div>
          <p className="meta"><Link to="/venues">Χώροι</Link></p>
          <h1>{detail?.venue.name ?? "Χώρος"}</h1>
          <p className="lede">{detail?.venue.ownership === "partner" ? "Χώρος συνεργάτη. Κενό ημερολόγιο σημαίνει άγνωστο, όχι διαθέσιμο." : "Εσωτερικός χώρος. Οι τιμές από κάτω είναι δείγμα, ώσπου να τις αντικαταστήσεις."}</p>
        </div>
        {dirty ? <span className="dirty">Αποθήκευτες αλλαγές</span> : null}
      </header>
      <Banner error={error} note={note} />
      {!detail && !error ? <p>Φόρτωση χώρου…</p> : null}
      {detail ? (
        <div className="split">
          <form
            className="panel"
            onChange={() => setDirty(true)}
            onSubmit={onSubmit(async (body) => {
              setError(null);
              try {
                await api(`/api/venues/${id}`, { method: "PATCH", body: JSON.stringify({ ...body, version: detail.venue.version }) });
                setNote("Τα στοιχεία του χώρου αποθηκεύτηκαν.");
                load();
              } catch (reason) {
                setError(messageOf(reason));
              }
            })}
          >
            <h2>Στοιχεία χώρου</h2>
            <div className="fields">
              <label>Ονομα<input name="name" defaultValue={detail.venue.name} key={detail.venue.version} /></label>
              <label>Περιοχή<input name="area" defaultValue={detail.venue.area ?? ""} /></label>
              <label>Email επαφής<input name="contact_email" defaultValue={detail.venue.contact_email ?? ""} /></label>
              <label>Περιορισμοί<input name="restrictions" defaultValue={detail.venue.restrictions ?? ""} /></label>
            </div>
            <label>Σημειώσεις<textarea name="notes" defaultValue={detail.venue.notes ?? ""} /></label>
            <div className="actions">
              <button type="submit">Αποθήκευση στοιχείων</button>
              <span className="meta">Εκδοση {detail.venue.version}. Αν κάποιος άλλος αποθήκευσε πρώτος, η σελίδα ζητά επαναφόρτωση.</span>
            </div>
          </form>
          <section className="panel">
            <h2>Εύρος ατόμων</h2>
            <p className="meta">Το μικρότερο και το μεγαλύτερο γκρουπ που χωρά αυτός ο χώρος.</p>
            {detail.spaces[0] ? (
              <form
                key={`${detail.spaces[0].id}-${detail.spaces[0].version}`}
                onSubmit={onSubmit(async (body) => {
                  setError(null);
                  const space = detail.spaces[0];
                  const minGuests = body.min_guests.trim() === "" ? null : Number(body.min_guests);
                  const maxGuests = body.max_guests.trim() === "" ? null : Number(body.max_guests);
                  if ((minGuests != null && !Number.isInteger(minGuests)) || (maxGuests != null && !Number.isInteger(maxGuests))) {
                    setError("Τα άτομα είναι ακέραιοι αριθμοί.");
                    return;
                  }
                  if (minGuests != null && maxGuests != null && minGuests > maxGuests) {
                    setError("Το εύρος ατόμων αρχίζει ψηλότερα από εκεί που τελειώνει.");
                    return;
                  }
                  try {
                    await api(`/api/spaces/${space.id}`, { method: "PATCH", body: JSON.stringify({ min_guests: minGuests, max_guests: maxGuests, version: space.version }) });
                    setNote(`Ο χώρος χωρά ${guestRange({ min_guests: minGuests, max_guests: maxGuests })}.`);
                    load();
                  } catch (reason) {
                    setError(messageOf(reason));
                  }
                })}
              >
                <div className="fields">
                  <label>Από<input name="min_guests" type="number" min={0} step={1} inputMode="numeric" defaultValue={detail.spaces[0].min_guests ?? ""} disabled={!canWrite} /></label>
                  <label>Εως<input name="max_guests" type="number" min={0} step={1} inputMode="numeric" defaultValue={detail.spaces[0].max_guests ?? ""} disabled={!canWrite} /></label>
                </div>
                {canWrite ? <button type="submit">Αποθήκευση εύρους</button> : <p className="meta">Αυτός ο ρόλος βλέπει το εύρος.</p>}
              </form>
            ) : null}
          </section>
        </div>
      ) : null}
      {financials ? (
        <section className="panel" style={{ marginTop: 16 }}>
          <h2>Τιμή πελάτη και εσωτερικό κόστος</h2>
          <p className="meta">Κενό κόστος μένει άγνωστο. Το μηδέν είναι ρητά δωρεάν κόστος. Η αποθήκευση δημοσιεύει νέα έκδοση τιμής και δεν πειράζει τις προτάσεις που έχουν ήδη σταλεί.</p>
          {[...published.values()].filter((rate) => rate.version_id).map((rate) => (
            <RateEditor key={rate.version_id} rate={rate} canPublish={canPublish} onSaved={(text) => { setNote(text); load(); }} onError={setError} />
          ))}
          {published.size === 0 ? <p>Δεν υπάρχουν κάρτες τιμών σε αυτόν τον χώρο. Πρόσθεσέ τες από τις Τιμές.</p> : null}
        </section>
      ) : <p className="banner">Τα οικονομικά πεδία είναι κρυφά για αυτόν τον ρόλο.</p>}
    </>
  );
}

function RateEditor({ rate, canPublish, onSaved, onError }: { rate: RateRow; canPublish: boolean; onSaved: (note: string) => void; onError: (message: string) => void }) {
  const [selling, setSelling] = useState(minorToEuros(rate.selling_price_minor));
  const [cost, setCost] = useState(minorToEuros(rate.internal_cost_minor));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSelling(minorToEuros(rate.selling_price_minor));
    setCost(minorToEuros(rate.internal_cost_minor));
  }, [rate.version_id, rate.selling_price_minor, rate.internal_cost_minor]);

  async function save() {
    const sellingMinor = eurosToMinor(selling);
    if (sellingMinor == null) {
      onError("Γράψε τιμή πελάτη σε ευρώ, για παράδειγμα 36.00.");
      return;
    }
    const costMinor = cost.trim() === "" ? null : eurosToMinor(cost);
    if (cost.trim() !== "" && costMinor == null) {
      onError("Το εσωτερικό κόστος μένει κενό ή είναι ποσό σε ευρώ. Βάλε 0.00 όταν το κόστος είναι γνωστά μηδέν.");
      return;
    }
    setBusy(true);
    onError("");
    try {
      const created = await api<{ id: string }>(`/api/rate-cards/${rate.id}/versions`, {
        method: "POST",
        body: JSON.stringify({
          selling_price_minor: sellingMinor,
          internal_cost_minor: costMinor,
          unit: rate.unit,
          currency: "EUR",
          days_of_week: rate.days_of_week ? JSON.parse(rate.days_of_week) : null,
          season_label: rate.season_label,
          valid_from: rate.valid_from,
          valid_to: rate.valid_to,
          tax_category_id: rate.version_tax,
        }),
      });
      await api(`/api/rate-versions/${created.id}/publish`, { method: "POST" });
      onSaved(`${rate.name} είναι πλέον ${formatMoney(sellingMinor)} για τον πελάτη${costMinor == null ? ", με άγνωστο εσωτερικό κόστος" : ` και ${formatMoney(costMinor)} εσωτερικό κόστος`}.`);
    } catch (reason) {
      onError(messageOf(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fields" style={{ alignItems: "end", marginTop: 8 }}>
      <label>{rate.name}
        <span className="meta">{rate.charge_role} · {rate.season_label || rate.days_of_week || "οποιαδήποτε μέρα"}</span>
      </label>
      <label>Τιμή πελάτη, EUR<input value={selling} onChange={(event) => setSelling(event.target.value)} inputMode="decimal" /></label>
      <label>Εσωτερικό κόστος, EUR<input value={cost} onChange={(event) => setCost(event.target.value)} inputMode="decimal" placeholder="Αγνωστο" /></label>
      <button type="button" disabled={!canPublish || busy} onClick={() => void save()}>{busy ? "Δημοσίευση…" : "Δημοσίευση τιμής"}</button>
    </div>
  );
}
